<template>
  <v-card>
    <v-card-text>
      <v-card-title>
        <h1 class="text-h4 mr-2">{{ title }}</h1>
        <v-icon @click="refreshList">mdi-refresh</v-icon>
        <v-spacer></v-spacer>
        <v-text-field @keydown.native.escape="search = ''" label="Search" single-line clearable hide-details
          v-model="search"></v-text-field>
      </v-card-title>
    </v-card-text>
    <v-data-table :headers="tableColumns" :items="queriedData" item-key="id" class="elevation-1">
      <template v-slot:item.is_template="{ item }">
        <v-icon v-if="!item.is_template">mdi-checkbox-blank-outline</v-icon>
        <v-icon v-if="item.is_template" color="green">mdi-checkbox-marked-outline</v-icon>
      </template>
      <template v-slot:item.global_template="{ item }">
        <v-icon v-if="item.global_template" color="primary">mdi-earth</v-icon>
      </template>
      <template v-slot:item.type="{ item }">
        <v-chip dark :color="templateTypeColor(item.type)">{{ templateTypeShort(item.type) }}</v-chip>
      </template>
      <template v-slot:item.actions="{ item }">
        <template v-if="item.is_template">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon v-on="on" medium class="mr-2" @click="handleNewFromTemplate(item)">mdi-content-copy</v-icon>
            </template>
            <span>Create New Email Template from Starter Template</span>
          </v-tooltip>
        </template>
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-icon v-on="on" medium class="mr-2" @click="handleEdit(item)">mdi-pencil</v-icon>
          </template>
          <span v-if="item.is_template">Edit Starter Template</span>
          <span v-if="!item.is_template">Edit Email Template</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-icon v-on="on" medium class="mr-2" @click="handleDelete(item)">mdi-delete</v-icon>
          </template>
          <span>Delete Email Template</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-icon v-on="on" medium class="mr-2" @click="handlePreview(item, false)">mdi-binoculars</v-icon>
          </template>
          <span>Preview Email Template</span>
        </v-tooltip>

        <v-tooltip top v-if="canPreviewTemplate(item)">
          <template v-slot:activator="{ on }">
            <v-icon v-on="on" medium class="mr-2" @click="handlePreview(item, true)">mdi-source-pull</v-icon>
          </template>
          <span>Preview Email Template with MergeTags included</span>
        </v-tooltip>
      </template>
    </v-data-table>
    <preview-template-dialog v-model="showPreviewDialog" :show-raw-html="previewShowRawHtml"
      :email-template-id="previewTemplateId" :email-footer-id="previewFooterId">
    </preview-template-dialog>
  </v-card>
</template>

<script>
import { mapState } from 'vuex'
import { EmailTemplateApi } from '@/services'
import { SearchFilterMixin } from '@/components/mixins'
import PreviewTemplateDialog from '@/components/email_builder/PreviewTemplateDialog'

export default {
  name: 'email-templates-list',
  mixins: [SearchFilterMixin],
  components: {
    [PreviewTemplateDialog.name]: PreviewTemplateDialog
  },
  props: {
    showTemplatesOnly: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: function () {
    return {
      search: '',
      propsToSearch: ['name', 'dealer.name', 'id'],
      templateFab: false,
      // NOTE IF tableColumnsBase changes, update computed tableColumns!
      tableColumnsBase: [
        { text: 'Template ID', value: 'id' },
        { text: 'Type', value: 'type' },
        { text: 'Name', value: 'name' },
        { text: 'Global Template', value: 'global_template' },
        { text: 'Starter Template', value: 'is_template' },
        { text: 'Actions', value: 'actions' }
      ],
      tableData: [],
      showPreviewDialog: false,
      previewTemplateId: null,
      previewFooterId: null,
      previewShowRawHtml: false
    }
  },
  computed: {
    ...mapState('DealerStore', ['dealer']),
    title() {
      if (this.showTemplatesOnly) {
        return 'Starter Templates'
      } else {
        return 'Email Templates'
      }
    },
    tableColumns() {
      // NOTE: is_tempalte and global_template are mutually exclusive - they can not both be true at the same time
      if (this.showTemplatesOnly) {
        const filteredTc = JSON.parse(JSON.stringify(this.tableColumnsBase))
        return filteredTc.filter(tc => {
          return tc.value !== 'global_template'
        })
      } else {
        const filteredTc = JSON.parse(JSON.stringify(this.tableColumnsBase))
        return filteredTc.filter(tc => {
          return tc.value !== 'is_template'
        })
      }
    },
    queriedData() {
      const viewData = this.tableData.filter(row => {
        if (this.showTemplatesOnly) {
          return row.is_template
        } else {
          return !row.is_template
        }
      })

      if (!this.search) {
        return viewData
      }
      return this.filterDataBySearchTerm(this.search, this.propsToSearch, viewData)
    }
  },
  watch: {
    dealer: function (newDealer, oldDealer) {
      this.refreshList()
    }
  },
  methods: {
    handleEdit(item) {
      const targetDealerId = item.dealer_id || this.dealer.id // || in case editing template
      this.$router.push({
        name: 'edit_email_builder_path',
        params: { id: item.id },
        query: { dealer_id: targetDealerId, type: item.type }
      })
    },
    handleNewFromTemplate(item) {
      this.$router.push({
        name: 'new_email_builder_path',
        query: { template_name: item.name, dealer_id: this.dealer.id, type: item.type }
      })
    },
    handleDelete(item) {
      const shouldDelete = confirm('Are you sure you want to delete? This action can not be undone.')
      if (shouldDelete) {
        EmailTemplateApi.deleteEmailTemplate(item.id)
          .then(() => {
            this.refreshList()
            this.$flash({
              showClose: true,
              message: 'Deleted Email Template',
              type: 'mini',
              duration: 2000
            })
          })
          .catch(error => {
            this.$flash({
              showClose: true,
              message: 'Error Deleting Email Templates. ' + error.message,
              type: 'error',
              duration: 0
            })
          })
      }
    },
    handlePreview(item, showRawHtml = false) {
      // BeeFree only saves JSON for starter templates - have to work around that to show a preview for starters
      if (item.is_template && item.type.includes('BeeFree')) {
        const targetDealerId = item.dealer_id || this.dealer.id // || in case editing template
        const path = this.$router.resolve({
          name: 'edit_email_builder_path',
          params: { id: item.id },
          query: { dealer_id: targetDealerId, type: item.type, previewMode: true }
        }).href

        const tab = window.open(window.location.origin + path, '_blank')
        tab.focus()
        return
      }

      this.previewTemplateId = item.id
      this.previewShowRawHtml = showRawHtml
      this.showPreviewDialog = true

      // EmailTemplateApi.getHtmlPreview(item.id, showRawHtml)
      //   .then((htmlTemplatePreview) => {
      //     const tab = window.open(htmlTemplatePreview.data.url, '_blank')
      //     tab.focus()
      //   })
      //   .catch(error => {
      //     this.$flash({
      //       showClose: true,
      //       message: 'Error Loading Email Templates. Reload to try again.' + error.message,
      //       type: 'error',
      //       duration: 0
      //     })
      //   })
    },
    refreshList() {
      return EmailTemplateApi.fetchEmailTemplates({ dealer_id: this.dealer.id })
        .then(response => {
          this.tableData = response.data
        })
        .catch(error => {
          this.$flash({
            showClose: true,
            message: 'Error Loading Email Templates. Reload to try again.' + error.message,
            type: 'error',
            duration: 0
          })
        })
    },
    templateTypeShort(templateType) {
      return templateType.replace('EmailTemplate', '')
    },
    templateTypeColor(templateType) {
      if (templateType.includes('BeeFree')) {
        return 'cyan'
      } else if (templateType.includes('Custom')) {
        return 'teal'
      } else {
        return 'black'
      }
    },
    canPreviewTemplate(item) {
      return !item.is_template || (item.is_template && !item.type.includes('BeeFree'))
    }
  },
  mounted: function () {
    return this.refreshList()
  }
}
</script>

<style></style>
